import { Listbox as ListBoxPrimitive, Transition } from '@headlessui/react'
import clsx from 'clsx'
import React, { Fragment, useEffect, type ReactNode } from 'react'
import { cn } from '#app/shared/lib/misc.js'
import { Separator } from '../../data-display/Separator.tsx'
import { ToolbarButton } from '../../data-display/Toolbar.tsx'
import { type AutocompleteOption } from '../../fields.tsx'
import { Icon } from '../../icons/Icon.tsx'
import {
  type InputSize,
  getOutlineLabelStyles,
  inputSizeOptions,
} from '../Input.tsx'

const SelectRoot = ListBoxPrimitive

type SelectOutlineLabelTriggerProps = {
  className?: string
  chevronIconClassName?: string
  placeholder?: string | number
  leftIcon?: React.ReactNode
  label: ReactNode
  value?: string | string[]
  open: boolean
  disabled?: boolean
  variant: 'outlined' | 'outlined-label' | 'filled' | 'ghost' | 'sexDollShop'
  size: InputSize
}
const SelectOutlineLabelButton = ({
  open,
  placeholder,
  className,
  chevronIconClassName,
  leftIcon,
  label,
  value,
  variant,
  disabled,
  size,
}: SelectOutlineLabelTriggerProps) => {
  const sizeClassName = inputSizeOptions[size]
  return (
    <ListBoxPrimitive.Button
      className={cn(
        selectContainerClassName(variant),
        sizeClassName,
        variant === 'sexDollShop' ? 'h-11 rounded-sm' : 'rounded-lg',
        variant === 'ghost' && 'border-none px-0 pr-7',
        disabled && '!cursor-auto !border !border-input',
        className,
      )}
      as={variant === 'outlined-label' ? 'fieldset' : 'button'}
      tabIndex={0}
    >
      {variant === 'outlined-label' && (
        <legend
          className={`
            invisible h-[5px] px-1 text-xs

            ${value || open ? 'block' : `hidden`}
          `}
        >
          {placeholder}
        </legend>
      )}
      <div className="flex gap-2">
        {leftIcon}
        {variant === 'outlined-label' ? (
          <>
            <div className={getOutlineLabelStyles({ leftIcon, value, open })}>
              {placeholder}
            </div>
            {value && (
              <div
                className={`
                  z-[3] mt-1 truncate whitespace-nowrap

                  ${!leftIcon && `pl-[5px]`}
                `}
              >
                {label}
              </div>
            )}
          </>
        ) : (
          <div
            className={cn(
              'z-[3] truncate whitespace-nowrap',
              !leftIcon && 'pl-[5px]',
              !value && 'text-foreground-muted',
            )}
          >
            {label}
          </div>
        )}
      </div>
      {!disabled && (
        <span
          className={`
            pointer-events-none absolute inset-y-0 right-0 flex items-center
            pr-2
          `}
        >
          <Icon
            name="basic/chevron-down"
            className={cn('h-4 w-4', chevronIconClassName)}
            aria-hidden="true"
          />
        </span>
      )}
    </ListBoxPrimitive.Button>
  )
}

const SelectToolbarButton = ({
  className,
  label,
}: {
  className?: string
  label: ReactNode
}) => {
  return (
    <ListBoxPrimitive.Button>
      <ToolbarButton
        className={cn(
          `
            flex items-center gap-2 outline-none

            focus-visible:outline-none
          `,
          className,
        )}
      >
        {label}
        <span
          className={`
            pointer-events-none absolute inset-y-0 right-0 flex items-center
            pr-2
          `}
        >
          <Icon
            name="basic/chevron-down"
            className="h-4 w-4 text-gray-400"
            aria-hidden="true"
          />
        </span>
      </ToolbarButton>
    </ListBoxPrimitive.Button>
  )
}

export const optionsDefaultClassName =
  'absolute z-[4] mt-1 max-h-[400px] w-full overflow-auto rounded-md bg-popover py-1 text-atoms shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm'

const SelectOptions = React.forwardRef<
  React.ElementRef<typeof ListBoxPrimitive.Options>,
  React.ComponentPropsWithoutRef<typeof ListBoxPrimitive.Options>
>(({ className, ...props }, ref) => (
  <ListBoxPrimitive.Options
    ref={ref}
    className={cn(optionsDefaultClassName, className)}
    {...props}
  />
))
SelectOptions.displayName = ListBoxPrimitive.Options.displayName

export const optionDefaultClassName = ({ active }: any) =>
  `relative cursor-default select-none py-2 pl-10 pr-4 ${
    active ? 'bg-primary-50 text-primary' : 'text-foreground'
  } aria-disabled:pointer-events-none aria-disabled:opacity-50 `

const SelectOption = React.forwardRef<
  React.ElementRef<typeof ListBoxPrimitive.Option>,
  Omit<
    React.ComponentPropsWithoutRef<typeof ListBoxPrimitive.Option>,
    'className'
  > & {
    className?: (active: boolean) => string
    variant?:
      | 'toolbar'
      | 'outlined'
      | 'outlined-label'
      | 'filled'
      | 'ghost'
      | 'sexDollShop'
  }
>(({ className, variant, ...props }, ref) => (
  <>
    <ListBoxPrimitive.Option
      ref={ref}
      className={({ active }: { active: boolean }) =>
        cn(optionDefaultClassName({ active }), className && className(active))
      }
      {...props}
    />
    {variant === 'sexDollShop' && <Separator className="my-1" />}
  </>
))
SelectOption.displayName = ListBoxPrimitive.Option.displayName

type ResetOptionProps = {
  labelText: string
  value?: string
  labelTrigger?: ReactNode
}
export const resetOption = ({
  labelText,
  value = '',
  labelTrigger,
}: ResetOptionProps) => {
  return {
    label: (
      <>
        <div className="opacity-50">{labelText}</div>
        <Separator className="absolute left-0 right-0 mt-1.5" />
      </>
    ),
    value,
    labelTrigger,
  }
}

const setInitialValue = ({
  value,
  defaultValue,
  multiple,
}: Pick<SelectProps, 'value' | 'defaultValue' | 'multiple'>) => {
  if (value) return value
  if (defaultValue) return defaultValue
  return multiple ? [] : ''
}

export type SelectProps = {
  className?: string
  value?: string | string[] | null
  defaultValue?: string | string[]
  onChange?: (value: string) => void
  placeholder?: string | number
  options: string[] | AutocompleteOption[]
  multiple?: boolean
  optionsClassName?: string
  optionClassName?: (active: boolean) => string
  triggerClassName?: string
  chevronIconClassName?: string
  checkIconClassName?: string
  variant?:
    | 'outlined'
    | 'outlined-label'
    | 'toolbar'
    | 'filled'
    | 'ghost'
    | 'sexDollShop'
  leftIcon?: React.ReactNode
  disabled?: boolean
  size?: InputSize
}

const Select = ({
  options,
  value: propValue,
  onChange,
  className,
  placeholder = 'Selecciona...',
  multiple,
  defaultValue,
  optionsClassName,
  optionClassName,
  triggerClassName,
  chevronIconClassName,
  checkIconClassName,
  variant = 'outlined',
  size = '55px',
  leftIcon,
  disabled = false,
}: SelectProps) => {
  const [value, setValue] = React.useState<string | string[]>(
    setInitialValue({ value: propValue, defaultValue, multiple }),
  )

  useEffect(() => {
    setValue(setInitialValue({ value: propValue, defaultValue, multiple }))
  }, [defaultValue, propValue, multiple])

  const handleChange = (currentValue: string | string[]) => {
    if (currentValue === value) return
    setValue(currentValue)

    if (onChange) {
      if (typeof currentValue === 'string') {
        onChange(currentValue)
      } else {
        onChange(currentValue.join(','))
      }
    }
  }

  const getOptionValue = (option: string | AutocompleteOption) => {
    if (typeof option == 'string') {
      return option
    }
    return option.value
  }
  const getOptionLabel = (option: string | AutocompleteOption) => {
    if (typeof option == 'string') {
      return option
    }
    return option.label
  }

  const getLabelTrigger = (
    value: string | string[] | null,
    options: string[] | AutocompleteOption[],
    placeholder: string,
  ) => {
    if (!value || value.length === 0) {
      return placeholder
    }

    // IF VALUES IS STRING
    if (typeof value === 'string') {
      if (typeof options[0] === 'string') {
        // options is array of strings
        return value
      }

      // options is array of objects
      const selectedOption = options.find((option) => {
        if (typeof option === 'string') {
          return option === value
        }
        return option.value === value
      })

      if (typeof selectedOption === 'string') {
        return selectedOption
      }

      const labelTrigger =
        selectedOption?.labelTrigger ?? selectedOption?.label ?? value
      return labelTrigger
    }

    // IF VALUES IS ARRAY
    if (typeof options[0] === 'string') {
      // options is array of strings
      return value.join(',')
    }

    // options is array of objects
    const labels = value.map((val) => {
      const selectedOption = options.find((option) => {
        if (typeof option === 'string') {
          return option === val
        }
        return option.value === val
      }) as AutocompleteOption

      const label = selectedOption?.label ?? val
      return String(label)
    })

    return labels.join(',')
  }

  return (
    <SelectRoot
      value={value}
      multiple={multiple}
      onChange={handleChange}
      disabled={disabled}
    >
      {({ open }) => (
        <div className={cn('relative w-full', className)}>
          {variant === 'toolbar' ? (
            <SelectToolbarButton
              label={getLabelTrigger(value, options, placeholder as string)}
              className={triggerClassName}
            />
          ) : (
            <SelectOutlineLabelButton
              chevronIconClassName={chevronIconClassName}
              open={open}
              placeholder={placeholder as any}
              leftIcon={leftIcon}
              className={triggerClassName}
              label={getLabelTrigger(value, options, placeholder as string)}
              variant={variant}
              value={value}
              disabled={disabled}
              size={size}
            />
          )}
          <Transition
            as={Fragment}
            enter="transition duration-100 ease-out"
            enterFrom="transform scale-95 opacity-0"
            enterTo="transform scale-100 opacity-100"
            leave="transition duration-75 ease-out"
            leaveFrom="transform scale-100 opacity-100"
            leaveTo="transform scale-95 opacity-0"
          >
            <SelectOptions className={cn(optionsClassName, 'bg-white')}>
              {options.map((option) => (
                <SelectOption
                  key={getOptionValue(option)}
                  value={getOptionValue(option)}
                  className={(active) =>
                    clsx(
                      active && variant === 'sexDollShop' && 'font-bold',
                      !active && variant !== 'sexDollShop' && 'font-normal',
                      optionClassName && optionClassName(active),
                    )
                  }
                  variant={variant}
                >
                  {({ selected }) => (
                    <>
                      <span
                        className={`
                          block truncate

                          ${
                            selected && variant === 'sexDollShop'
                              ? `text-primary-600`
                              : ''
                          }
                        `}
                      >
                        {getOptionLabel(option)}
                      </span>
                      {selected ? (
                        <span
                          className={`
                            absolute inset-y-0 left-0 flex items-center pl-3
                            text-[#CC3BB1]
                          `}
                        >
                          {selected && variant !== 'sexDollShop' && (
                            <div
                              className={`
                                flex h-5 w-5 items-center justify-center
                                rounded-full border-2 border-primary
                                border-opacity-15
                              `}
                            >
                              <Icon
                                name="basic/check"
                                size="12px"
                                strokeWidth={1.1}
                                className={cn('text-white', checkIconClassName)}
                                aria-hidden="true"
                              />
                            </div>
                          )}
                        </span>
                      ) : null}
                    </>
                  )}
                </SelectOption>
              ))}
            </SelectOptions>
          </Transition>
        </div>
      )}
    </SelectRoot>
  )
}

export const selectContainerClassName = (variant: string) => `
  data-[headlessui-state=open]>legend:block relative flex h-14 w-full cursor-pointer
  items-center justify-between border border-input px-4
   focus-visible:!outline-none

  ${
    variant === 'sexDollShop'
      ? 'data-[headlessui-state=open]:bg-[#D9D9DE]'
      : `data-[headlessui-state=open]:border-foreground data-[headlessui-state=open]:border-2
          hover:border-foreground
          focus:border-foreground focus-visible:border-foreground focus:border-2
        `
  }
`

export {
  SelectRoot,
  SelectOutlineLabelButton,
  SelectOptions,
  SelectOption,
  Select,
}
